import Phaser from 'phaser';
import { COLORS, GAME_HEIGHT, GAME_WIDTH } from '../config/constants';
import { audio } from '../core/AudioSynth';
import { voice } from '../core/Voice';
import { addAudioToggleButtons, addFullscreenIconButton, addRetroButtonHover, drawScanlines } from '../ui/Retro';
import { applyUiTextScale } from '../ui/UiText';
import { RuntimeAssetsScene } from './RuntimeAssetsScene';

type StoryMode = 'intro' | 'credits';

const INTRO_SEEN_KEY = 'apogee-hold-intro-seen-v1';

const STORY_PAGES = [
  {
    title: 'THE LAST HOLD',
    body: 'The colonies did not fall in a single night.\nOne relay went dark, then one reactor,\nthen one bastion at a time.\n\nBeneath the ice, slag and abandoned\ninfrastructure, the Undermesh woke —\nand it is moving toward the last human lights.',
    detail: 'THE SIGNAL IS WEAK. THE LINE IS NOT.',
    art: 'story-kappa',
    caption: 'BASTION-7 · OUTPOST KAPPA',
  },
  {
    title: 'YOUR COMMAND',
    body: 'You are the commander of Bastion-7.\nBuild a living defence line, place each\ntower with purpose, and read the enemy\nbefore the next wave arrives.\n\nEvery lane is a decision. Every breach\nleaves a mark.',
    detail: 'DEPLOY · ADAPT · HOLD THE CORE',
    art: 'story-aurora',
    caption: 'A FIELD OF ROUTES · A HUNDRED WAYS TO FAIL',
  },
  {
    title: 'THE LONG WAR',
    body: 'Campaign missions teach the shape of the\nthreat. Research turns hard lessons into\npermanent advantages. Survival mode never\nstops asking questions.\n\nHold long enough, and the colony becomes\nmore than a place on a map — it becomes yours.',
    detail: 'THE MESH LEARNS. SO DO YOU.',
    art: 'story-emberfall',
    caption: 'SURVIVAL · ENDLESS WAVES · ONE COMMAND CORE',
  },
];

const SCREENSHOTS = [
  { key: 'story-kappa', title: 'OUTPOST KAPPA', caption: 'The first line of defence.' },
  { key: 'story-aurora', title: 'AURORA CITADEL', caption: 'Routes, sightlines and pressure.' },
  { key: 'story-emberfall', title: 'EMBER VAULT', caption: 'The longer the wave, the less forgiving the field.' },
];

export class StoryScene extends Phaser.Scene {
  private mode: StoryMode = 'intro';
  private page = 0;
  private returnTo = 'Menu';
  private markSeen = false;

  constructor() {
    super('Story');
  }

  preload(): void {
    this.load.image('story-kappa', 'assets/art/battle-kappa-bg.png');
    this.load.image('story-aurora', 'assets/art/battle-aurora-bg.png');
    this.load.image('story-emberfall', 'assets/art/battle-emberfall-bg.png');
  }

  init(data?: { mode?: StoryMode; returnTo?: string; markSeen?: boolean }): void {
    this.mode = data?.mode ?? 'intro';
    this.returnTo = data?.returnTo ?? 'Menu';
    this.markSeen = data?.markSeen === true;
    this.page = 0;
  }

  create(): void {
    this.cameras.main.fadeIn(220, 0, 0, 0);
    this.input.keyboard?.on('keydown-LEFT', this.previous, this);
    this.input.keyboard?.on('keydown-RIGHT', this.next, this);
    this.input.keyboard?.on('keydown-ESC', this.close, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.keyboard?.off('keydown-LEFT', this.previous, this);
      this.input.keyboard?.off('keydown-RIGHT', this.next, this);
      this.input.keyboard?.off('keydown-ESC', this.close, this);
    });
    this.render();
    if (this.mode === 'intro') {
      void RuntimeAssetsScene.ensureStarted(this).then(() => this.playIntroVoice());
    }
  }

  private playIntroVoice(): void {
    if (this.mode !== 'intro') return;
    const id = `story_intro_${this.page + 1}`;
    if (!this.cache.audio.exists(`voice-${id}`)) {
      void RuntimeAssetsScene.ensureStarted(this).then(() => {
        voice.play(this, id, { priority: true, cooldown: 0 });
      });
      return;
    }
    voice.play(this, id, { priority: true, cooldown: 0 });
  }

  private render(): void {
    this.children.removeAll(true);
    const g = this.add.graphics();
    const artKey = this.mode === 'intro' ? STORY_PAGES[this.page].art : 'story-aurora';
    const art = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, artKey);
    const scale = Math.max(GAME_WIDTH / art.width, GAME_HEIGHT / art.height);
    art.setScale(scale * 1.08).setAlpha(0.52);
    g.fillGradientStyle(0x05090d, 0x05090d, 0x05090d, 0x05090d, 0.94, 0.54, 0.82, 0.9);
    g.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    drawScanlines(g, GAME_WIDTH, GAME_HEIGHT, 0.05, 4);
    this.drawHeader(g);
    if (this.mode === 'intro') this.renderIntro(g);
    else this.renderCredits(g);
    addAudioToggleButtons(this, 46, 28);
    addFullscreenIconButton(this, GAME_WIDTH - 48, 28);
    applyUiTextScale(this);
  }

  private drawHeader(g: Phaser.GameObjects.Graphics): void {
    g.fillStyle(0x080f16, 0.9).fillRect(0, 0, GAME_WIDTH, 66);
    g.lineStyle(1, 0x31566a, 1).lineBetween(0, 66, GAME_WIDTH, 66);
    g.fillStyle(COLORS.accent, 0.85).fillRect(0, 64, 230, 2);
    this.add.text(34, 22, 'AEGIS RESPONSE COMMAND', {
      fontFamily: 'Consolas, monospace', fontSize: '12px', color: '#6a9cad', letterSpacing: 3,
    });
    this.add.text(GAME_WIDTH - 34, 22, this.mode === 'intro' ? 'ARCHIVE BRIEFING · 01' : 'ARCHIVE BRIEFING · CREDITS', {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#d4a24a',
    }).setOrigin(1, 0);
    this.add.text(34, 44, this.mode === 'intro' ? 'APOGEE HOLD · FIELD INTRODUCTION' : 'APOGEE HOLD · SIGNAL CONTRIBUTORS', {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9ad4e8', letterSpacing: 1,
    });
  }

  private renderIntro(g: Phaser.GameObjects.Graphics): void {
    const page = STORY_PAGES[this.page];
    this.add.text(74, 108, page.title, {
      fontFamily: 'Consolas, monospace', fontSize: '31px', color: '#e4f7ff', fontStyle: 'bold', letterSpacing: 2,
    });
    this.add.text(76, 150, page.detail, {
      fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#d4a24a', letterSpacing: 2,
    });
    g.fillStyle(0x081722, 0.92).fillRoundedRect(68, 186, 590, 360, 8);
    g.lineStyle(1.5, COLORS.accent, 0.82).strokeRoundedRect(68, 186, 590, 360, 8);
    g.lineStyle(1, 0xb2f2ff, 0.18).lineBetween(88, 234, 638, 234);
    this.add.text(94, 258, page.body, {
      fontFamily: 'Consolas, monospace', fontSize: '16px', color: '#c8d4e0',
      lineSpacing: 10,
    }).setFixedSize(526, 205);
    this.add.text(94, 498, 'THE COLONY IS LISTENING.', {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#6a9cad', letterSpacing: 2,
    });
    this.drawScreenshot(g, page.art, 718, 186, 472, 300, page.caption);
    this.add.text(718, 510, `ARCHIVE PAGE 0${this.page + 1} / 0${STORY_PAGES.length}`, {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#6a9cad', letterSpacing: 1,
    });
    this.drawFooterButtons(
      this.page === 0 ? undefined : { label: 'BACK', action: () => this.previous() },
      this.page === STORY_PAGES.length - 1
        ? { label: 'ENTER COMMAND DECK', action: () => this.close() }
        : { label: 'CONTINUE', action: () => this.next() },
      { label: 'SKIP INTRO', action: () => this.close() },
    );
  }

  private renderCredits(g: Phaser.GameObjects.Graphics): void {
    this.add.text(74, 108, 'SIGNAL CONTRIBUTORS', {
      fontFamily: 'Consolas, monospace', fontSize: '31px', color: '#e4f7ff', fontStyle: 'bold', letterSpacing: 2,
    });
    this.add.text(76, 150, 'EVERY HOLD HAS MORE THAN ONE VOICE', {
      fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#d4a24a', letterSpacing: 2,
    });
    g.fillStyle(0x081722, 0.94).fillRoundedRect(68, 186, 560, 430, 8);
    g.lineStyle(1.5, COLORS.accent, 0.82).strokeRoundedRect(68, 186, 560, 430, 8);
    this.creditText(96, 222, 'CREATED BY', 'BigAl', '#e4f7ff', 22);
    this.creditText(96, 286, 'WITH LOVE AND PATIENCE', 'My Wife', '#d4a24a', 18);
    this.creditText(96, 350, 'UNEXPECTEDLY IMPORTANT SUPPORT', 'Skye · who helped more than he realised', '#9ad4e8', 15);
    this.add.text(96, 438, 'TOOLS THAT JOINED THE COMMAND DECK', {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#6a9cad', letterSpacing: 1,
    });
    this.add.text(96, 466, 'Xiaomi Mimo  ·  Mimo TTS\nOpenAI Codex  ·  DeepSeek  ·  Suno', {
      fontFamily: 'Consolas, monospace', fontSize: '15px', color: '#c8d4e0', lineSpacing: 12,
    });
    this.add.text(96, 548, 'MORE SIGNALS TO COME — IF SATISFIED PLAYERS JOIN THE HOLD.', {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#7dce5a', letterSpacing: 1,
    });
    this.add.text(682, 186, 'FIELD RECORDS', {
      fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#d4a24a', letterSpacing: 2,
    });
    SCREENSHOTS.forEach((shot, index) => {
      const y = 220 + index * 122;
      this.drawCreditRecord(g, shot.key, 682, y, shot.title, shot.caption);
    });
    this.drawFooterButtons(
      { label: 'INTRO', action: () => { this.mode = 'intro'; this.page = 0; this.render(); } },
      { label: 'RETURN TO MENU', action: () => this.close() },
    );
  }

  private creditText(x: number, y: number, label: string, value: string, color: string, size: number): void {
    this.add.text(x, y, label, { fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#6a9cad', letterSpacing: 1 });
    this.add.text(x, y + 22, value, { fontFamily: 'Consolas, monospace', fontSize: `${size}px`, color });
  }

  private drawScreenshot(g: Phaser.GameObjects.Graphics, key: string, x: number, y: number, w: number, h: number, caption: string): void {
    g.fillStyle(0x071017, 0.96).fillRoundedRect(x - 6, y - 6, w + 12, h + 40, 6);
    g.lineStyle(1, 0x5ec8ff, 0.6).strokeRoundedRect(x - 6, y - 6, w + 12, h + 40, 6);
    const image = this.add.image(x + w / 2, y + h / 2, key);
    image.setDisplaySize(w, h).setAlpha(0.88);
    this.add.text(x, y + h + 12, caption, {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9ad4e8', letterSpacing: 1,
    });
  }

  private drawCreditRecord(g: Phaser.GameObjects.Graphics, key: string, x: number, y: number, title: string, caption: string): void {
    const imageW = 184;
    const imageH = 104;
    g.fillStyle(0x071017, 0.96).fillRoundedRect(x - 6, y - 6, 466, imageH + 12, 6);
    g.lineStyle(1, 0x5ec8ff, 0.6).strokeRoundedRect(x - 6, y - 6, 466, imageH + 12, 6);
    const image = this.add.image(x + imageW / 2, y + imageH / 2, key);
    image.setDisplaySize(imageW, imageH).setAlpha(0.88);
    this.add.text(x + 210, y + 28, title, {
      fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#d4a24a', letterSpacing: 1,
    });
    this.add.text(x + 210, y + 52, caption, {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#9ad4e8', wordWrap: { width: 220 },
    });
  }

  private drawFooterButtons(...buttons: Array<{ label: string; action: () => void } | undefined>): void {
    const visible = buttons.filter((button): button is { label: string; action: () => void } => Boolean(button));
    const width = 160;
    const gap = 12;
    const total = visible.length * width + (visible.length - 1) * gap;
    let x = (GAME_WIDTH - total) / 2 + width / 2;
    visible.forEach((item, index) => {
      const button = this.add.rectangle(x, 666, width, 34, index === visible.length - 1 ? 0x152028 : 0x1a2830, 1)
        .setStrokeStyle(1, index === visible.length - 1 ? 0x496878 : COLORS.accent)
        .setInteractive({ useHandCursor: true });
      addRetroButtonHover(button);
      this.add.text(x, 666, item.label, {
        fontFamily: 'Consolas, monospace', fontSize: '10px', color: index === visible.length - 1 ? '#9ad4e8' : '#5ec8ff',
      }).setOrigin(0.5);
      button.on('pointerdown', () => { audio.click(); item.action(); });
      x += width + gap;
    });
  }

  private previous = (): void => {
    if (this.mode !== 'intro') return;
    this.page = Math.max(0, this.page - 1);
    audio.click();
    this.render();
    this.playIntroVoice();
  };

  private next = (): void => {
    if (this.mode !== 'intro') return;
    if (this.page >= STORY_PAGES.length - 1) { this.close(); return; }
    this.page += 1;
    audio.click();
    this.render();
    this.playIntroVoice();
  };

  private close = (): void => {
    if (this.mode === 'intro' && this.markSeen) localStorage.setItem(INTRO_SEEN_KEY, '1');
    this.scene.stop();
    this.scene.resume(this.returnTo);
    const menu = this.scene.get(this.returnTo) as Phaser.Scene & { showProfileAfterIntro?: () => void };
    if (this.mode === 'intro') menu.showProfileAfterIntro?.();
  };
}

export { INTRO_SEEN_KEY };
